// =====================================================
// AttachmentsList Component
// AdminMunicipal v6.0 - Document Storage System
// =====================================================

import React, { useState, useEffect } from 'react';
import { Download, Trash2, File, FileText, Image, Video, Music, Archive, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useToast } from '../../contexts/ToastContext';
import { logger } from '../../lib/logger';

interface Attachment {
  id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  uploaded_at: string;
  r2_bucket_name: string;
  r2_storage_path: string;
}

interface AttachmentsListProps {
  entityType: 'propuesta' | 'gestion' | 'reunion';
  entityId: string;
  onAttachmentDeleted?: () => void;
  refreshTrigger?: number; // External trigger to refresh list
}

export const AttachmentsList: React.FC<AttachmentsListProps> = ({
  entityType,
  entityId,
  onAttachmentDeleted,
  refreshTrigger = 0,
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const { currentOrg } = useOrganization();
  const toast = useToast();

  // Fetch attachments
  const fetchAttachments = async () => {
    if (!entityId) return;

    setLoading(true);
    try {
      const attachmentTableMap = {
        propuesta: 'propuesta_attachments',
        gestion: 'gestion_attachments',
        reunion: 'reunion_attachments',
      };

      const entityIdFieldMap = {
        propuesta: 'propuesta_id',
        gestion: 'gestion_id',
        reunion: 'reunion_id',
      };

      const { data, error } = await supabase
        .from(attachmentTableMap[entityType] as any)
        .select('*')
        .eq(entityIdFieldMap[entityType], entityId)
        .order('uploaded_at', { ascending: false });

      if (error) {
        logger.error('[AttachmentsList] Error fetching attachments', error);
        setError('Error al cargar los archivos adjuntos');
        return;
      }

      setAttachments((data || []) as unknown as Attachment[]);
    } catch (error) {
      logger.error('[AttachmentsList] Error fetching attachments', error);
      setError('Error al cargar los archivos adjuntos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [entityId, entityType, refreshTrigger]);

  // Get file icon based on MIME type
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Image className="w-5 h-5 text-purple-600" />;
    } else if (mimeType.startsWith('video/')) {
      return <Video className="w-5 h-5 text-red-600" />;
    } else if (mimeType.startsWith('audio/')) {
      return <Music className="w-5 h-5 text-green-600" />;
    } else if (
      mimeType.includes('zip') ||
      mimeType.includes('rar') ||
      mimeType.includes('7z') ||
      mimeType.includes('tar')
    ) {
      return <Archive className="w-5 h-5 text-yellow-600" />;
    } else if (
      mimeType.includes('pdf') ||
      mimeType.includes('document') ||
      mimeType.includes('text')
    ) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    return <File className="w-5 h-5 text-gray-600" />;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Download attachment
  const handleDownload = async (attachment: Attachment) => {
    if (!user) return;

    setDownloadingIds((prev) => new Set(prev).add(attachment.id));

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      // Get presigned download URL
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-download-url`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attachmentId: attachment.id,
            entityType,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al generar URL de descarga');
      }

      const { downloadUrl } = await response.json();

      // Use blob method for ALL platforms to force download instead of browser display
      // This ensures files download properly on desktop, iOS, and Android
      try {
        const fileResponse = await fetch(downloadUrl);
        if (!fileResponse.ok) {
          throw new Error('Failed to fetch file');
        }

        const blob = await fileResponse.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachment.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (blobError) {
        logger.error('Blob download failed, trying direct link:', blobError);
        // Fallback to direct link if blob method fails
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      logger.error('Download error:', error);
      // Show user-friendly message, don't expose internal error details
      toast.error('Error al descargar', 'No se pudo descargar el archivo. Por favor, intenta de nuevo.');
    } finally {
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  // Delete attachment
  const handleDelete = async (attachment: Attachment) => {
    if (!user) return;

    // Confirm deletion
    if (!confirm(`¿Estás seguro de eliminar "${attachment.file_name}"?`)) {
      return;
    }

    setDeletingIds((prev) => new Set(prev).add(attachment.id));

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      // Call delete Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-file`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attachmentId: attachment.id,
            entityType,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar el archivo');
      }

      // Remove from local state
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));

      if (onAttachmentDeleted) {
        onAttachmentDeleted();
      }
    } catch (error) {
      logger.error('Delete error:', error);
      // Show user-friendly message, don't expose internal error details
      toast.error('Error al eliminar', 'No se pudo eliminar el archivo. Por favor, intenta de nuevo.');
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  // Check if user can delete (owner or admin)
  const canDelete = (attachment: Attachment): boolean => {
    if (!user || !currentOrg) return false;
    // User is the uploader or has admin role
    // Note: We'd need to check org membership role, but for now assume uploaded_by check
    return attachment.uploaded_by === user.id;
    // TODO: Add admin role check from organization_members table
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-[#B89658] animate-spin" />
        <span className="ml-2 text-sm text-gray-600">Cargando archivos adjuntos...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 border-2 border-dashed border-red-200 rounded-lg bg-red-50">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={() => { setError(null); fetchAttachments(); }}
          className="mt-3 px-4 py-2 text-sm bg-[#5D1828] text-white rounded-lg hover:bg-[#7D2838] transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Empty state
  if (attachments.length === 0) {
    return (
      <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
        <File className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No hay archivos adjuntos</p>
        <p className="text-xs text-gray-400 mt-1">Sube archivos usando el área de arriba</p>
      </div>
    );
  }

  // List of attachments
  return (
    <div className="space-y-2">
      <AnimatePresence>
        {attachments.map((attachment) => (
          <motion.div
            key={attachment.id}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex items-center p-3 bg-white border border-gray-200 rounded-lg hover:border-[#B89658]/30 transition-colors"
          >
            {/* File Icon */}
            <div className="p-2 bg-gray-50 rounded-lg">
              {getFileIcon(attachment.mime_type)}
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0 ml-3">
              <p className="text-sm font-medium text-gray-900 truncate">
                {attachment.file_name}
              </p>
              <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
                <span>{formatFileSize(attachment.file_size)}</span>
                <span>•</span>
                <span>{formatDate(attachment.uploaded_at)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 ml-3">
              {/* Download Button */}
              <button
                onClick={() => handleDownload(attachment)}
                disabled={downloadingIds.has(attachment.id)}
                className="p-2 text-gray-600 hover:text-[#B89658] hover:bg-[#B89658]/5 rounded-lg transition-colors disabled:opacity-50"
                title="Descargar"
              >
                {downloadingIds.has(attachment.id) ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </button>

              {/* Delete Button */}
              {canDelete(attachment) && (
                <button
                  onClick={() => handleDelete(attachment)}
                  disabled={deletingIds.has(attachment.id)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Eliminar"
                >
                  {deletingIds.has(attachment.id) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
